import React, { useEffect, useRef, useState } from "react";
import { Button, Select } from "antd";
import { controller as rootController } from "../../../../controller";

/**
 * MemberDobSuggestion — when an insurance policy's Member DOB is empty, look up
 * same-office patients matching the member's name and offer to apply their DOB.
 *
 * The office is derived server-side from `policyId`/`patientId` (office-context-
 * safe — we never send the client office); we only pass the id(s) + the member
 * name. `onUse(dob)` lets the host decide what "use" means: an immediate save in
 * the inline collapse, or filling a form field in the Manage-Policies form.
 *
 * Fetches (debounced) only while `active` (DOB empty) and both names are present,
 * and renders nothing unless there is at least one matching candidate.
 */
export default function MemberDobSuggestion({ patientId, policyId, first, last, active, onUse }) {
  const [candidates, setCandidates] = useState([]);
  const [selectedDob, setSelectedDob] = useState(undefined);
  const timerRef = useRef(null);

  const f = (first || "").trim();
  const l = (last || "").trim();
  const shouldFetch = !!(active && f && l && (policyId != null || patientId != null));

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!shouldFetch) {
      setCandidates([]);
      setSelectedDob(undefined);
      return undefined;
    }
    let cancelled = false;
    timerRef.current = setTimeout(async () => {
      try {
        const res = await rootController.getMemberDobCandidates({ patientId, policyId, first: f, last: l });
        const list =
          res && res.status >= 200 && res.status < 300 && res.json && Array.isArray(res.json.candidates)
            ? res.json.candidates
            : [];
        if (!cancelled) {
          setCandidates(list);
          setSelectedDob(list.length ? list[0].date_of_birth : undefined);
        }
      } catch (e) {
        if (!cancelled) setCandidates([]);
      }
    }, 400);
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetch, f, l, policyId, patientId]);

  if (!candidates.length) return null;

  const single = candidates.length === 1 ? candidates[0] : null;
  const chosen = single ? single.date_of_birth : selectedDob;

  return (
    <div
      style={{
        marginTop: 4,
        padding: "6px 10px",
        borderRadius: 8,
        background: "rgba(107,67,181,0.06)",
        border: "1px solid rgba(107,67,181,0.18)",
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
        fontSize: 12.5,
      }}
    >
      <span role="img" aria-label="suggestion">💡</span>
      {single ? (
        <span style={{ color: "#5b4a7e" }}>
          <strong>{[single.first_name, single.last_name].filter(Boolean).join(" ")}</strong>
          {" is a patient here — born "}
          <strong>{single.date_of_birth}</strong>.
        </span>
      ) : (
        <>
          <span style={{ color: "#5b4a7e" }}>Matching patients found — pick a DOB:</span>
          <Select
            size="small"
            value={selectedDob}
            onChange={setSelectedDob}
            style={{ minWidth: 220 }}
            options={candidates.map((c) => ({
              value: c.date_of_birth,
              label: `${[c.first_name, c.last_name].filter(Boolean).join(" ")} — ${c.date_of_birth}`,
            }))}
          />
        </>
      )}
      <Button
        type="link"
        size="small"
        style={{ padding: 0, height: "auto" }}
        onClick={() => chosen && onUse(chosen)}
        disabled={!chosen}
      >
        Use
      </Button>
    </div>
  );
}
